import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Upload } from 'lucide-react';

const EditProposal = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [quantity, setQuantity] = useState(24);
  const [brand, setBrand] = useState('gildan');
  const [style, setStyle] = useState('5000');
  const [color, setColor] = useState('Black');
  const [sizes, setSizes] = useState({ S: 0, M: 0, L: 0, XL: 0, '2XL': 0, '3XL': 0 });
  const [printLocations, setPrintLocations] = useState([]);
  const [colorsPerLocation, setColorsPerLocation] = useState({});
  const [printMethod, setPrintMethod] = useState('screen_print');
  const [notes, setNotes] = useState('');
  const [logoImage, setLogoImage] = useState(null);

  useEffect(() => {
    fetchProposal();
  }, [id]);

  const fetchProposal = async () => {
    try {
      const response = await axios.get(`/proposals/${id}`);
      const proposal = response.data;
      
      setCustomerName(proposal.customer_name);
      setCustomerEmail(proposal.customer_email);
      setCustomerPhone(proposal.customer_phone || '');
      setQuantity(proposal.quantity);
      setNotes(proposal.notes || '');
      setPrintLocations(proposal.print_details.locations);
      setColorsPerLocation(proposal.print_details.colors_per_location);
      setPrintMethod(proposal.print_details.method);
      
      if (proposal.items && proposal.items.length > 0) {
        const item = proposal.items[0];
        setBrand(item.brand);
        setStyle(item.style);
        setColor(item.color);
        setSizes(item.sizes || { S: 0, M: 0, L: 0, XL: 0, '2XL': 0, '3XL': 0 });
      }
      
      if (proposal.mockup_url) {
        setLogoImage(proposal.mockup_url);
      }
    } catch (error) {
      toast.error('Failed to load proposal');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationToggle = (location) => {
    if (printLocations.includes(location)) {
      setPrintLocations(printLocations.filter(l => l !== location));
      const newColors = { ...colorsPerLocation };
      delete newColors[location];
      setColorsPerLocation(newColors);
    } else {
      setPrintLocations([...printLocations, location]);
      setColorsPerLocation({ ...colorsPerLocation, [location]: 1 });
    }
  };

  const handleColorChange = (location, colors) => {
    setColorsPerLocation({ ...colorsPerLocation, [location]: parseInt(colors) });
  };

  const handleSizeChange = (size, value) => {
    setSizes({ ...sizes, [size]: parseInt(value) || 0 });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        if (!reader.result) {
          toast.error('Failed to read file');
          return;
        }
        const base64 = reader.result.split(',')[1];
        setLogoImage(base64);
        
        // Upload logo to proposal
        try {
          const formData = new FormData();
          formData.append('logo_base64', base64);
          await axios.put(`/proposals/${id}/logo`, formData);
          toast.success('Logo uploaded!');
        } catch (error) {
          toast.error('Failed to upload logo to server');
        }
      } catch (error) {
        console.error('Error processing image:', error);
        toast.error('Failed to process image');
      }
    };
    reader.onerror = () => {
      console.error('FileReader error');
      toast.error('Failed to read file');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!customerName || !customerEmail || printLocations.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const proposalData = {
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        quantity: quantity,
        items: [{
          brand: brand,
          style: style,
          color: color,
          sizes: sizes,
          base_price: 3.25,
          markup_price: 6.50,
          image_url: null
        }],
        print_details: {
          locations: printLocations,
          colors_per_location: colorsPerLocation,
          ink_colors: [],
          method: printMethod,
          stitch_count: printMethod === 'embroidery' ? 5000 : null
        },
        notes: notes
      };

      await axios.put(`/proposals/${id}`, proposalData);
      toast.success('Proposal updated successfully!');
      navigate(`/proposal/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update proposal');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  return (
    <div className="quote-builder" data-testid="edit-proposal">
      <div className="builder-container">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
          <Button
            variant="ghost"
            onClick={() => navigate(`/proposal/${id}`)}
            data-testid="back-button"
          >
            <ArrowLeft size={18} />
          </Button>
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0 }} data-testid="page-title">
            Edit Proposal
          </h1>
        </div>

        {/* Customer Information */}
        <div className="form-section">
          <h3>Customer Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <Label htmlFor="customerName">Customer Name *</Label>
              <Input
                id="customerName"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                required
                data-testid="customer-name-input"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="customerEmail">Email *</Label>
              <Input
                id="customerEmail"
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                required
                data-testid="customer-email-input"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="customerPhone">Phone</Label>
              <Input
                id="customerPhone"
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                data-testid="customer-phone-input"
              />
            </div>
          </div>
        </div>

        {/* Shirt Selection */}
        <div className="form-section">
          <h3>Shirt Selection</h3>
          <div className="form-grid">
            <div className="form-group">
              <Label htmlFor="quantity">Quantity *</Label>
              <Input
                id="quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value))}
                min="12"
                data-testid="quantity-input"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="brand">Brand</Label>
              <Select value={brand} onValueChange={setBrand}>
                <SelectTrigger id="brand">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gildan">Gildan</SelectItem>
                  <SelectItem value="nextlevel">Next Level</SelectItem>
                  <SelectItem value="bellacanvas">Bella + Canvas</SelectItem>
                  <SelectItem value="comfortcolors">Comfort Colors</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <Label htmlFor="style">Style</Label>
              <Input
                id="style"
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                data-testid="style-input"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="color">Color</Label>
              <Select value={color} onValueChange={setColor}>
                <SelectTrigger id="color">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Black">Black</SelectItem>
                  <SelectItem value="White">White</SelectItem>
                  <SelectItem value="Navy">Navy</SelectItem>
                  <SelectItem value="Red">Red</SelectItem>
                  <SelectItem value="Royal Blue">Royal Blue</SelectItem>
                  <SelectItem value="Forest Green">Forest Green</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div style={{ marginTop: '16px' }}>
            <Label>Sizes</Label>
            <div className="form-grid">
              {Object.keys(sizes).map((size) => (
                <div className="form-group" key={size}>
                  <Label htmlFor={`size-${size}`}>{size}</Label>
                  <Input
                    id={`size-${size}`}
                    type="number"
                    value={sizes[size]}
                    onChange={(e) => handleSizeChange(size, e.target.value)}
                    min="0"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Print Details */}
        <div className="form-section">
          <h3>Print Details</h3>
          <div className="form-group" style={{ marginBottom: '16px' }}>
            <Label>Print Method</Label>
            <Select value={printMethod} onValueChange={setPrintMethod}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="screen_print">Screen Print</SelectItem>
                <SelectItem value="embroidery">Embroidery</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="form-group">
            <Label>Print Locations *</Label>
            <div className="checkbox-group">
              {['front', 'back', 'left_sleeve', 'right_sleeve'].map((location) => (
                <label key={location} className="checkbox-label">
                  <Checkbox
                    checked={printLocations.includes(location)}
                    onCheckedChange={() => handleLocationToggle(location)}
                  />
                  <span>{location.replace('_', ' ').toUpperCase()}</span>
                </label>
              ))}
            </div>
          </div>

          {printLocations.map((location) => (
            <div className="form-group" key={location} style={{ marginTop: '16px' }}>
              <Label htmlFor={`colors-${location}`}>
                Colors for {location.replace('_', ' ').toUpperCase()}
              </Label>
              <Input
                id={`colors-${location}`}
                type="number"
                value={colorsPerLocation[location] || 1}
                onChange={(e) => handleColorChange(location, e.target.value)}
                min="1"
                max="6"
              />
            </div>
          ))}
        </div>

        {/* Logo Upload */}
        <div className="form-section">
          <h3>Company/Design Logo</h3>
          <div className="form-group">
            <Label htmlFor="logo-upload">Upload Logo (Will appear on PDF)</Label>
            <input
              id="logo-upload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="form-input"
              data-testid="logo-upload-input"
              style={{ 
                padding: '10px 14px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                width: '100%',
                cursor: 'pointer'
              }}
            />
          </div>
          {logoImage && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <img
                src={`data:image/png;base64,${logoImage}`}
                alt="Logo Preview"
                style={{ maxWidth: '200px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              />
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="form-section">
          <h3>Additional Notes</h3>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any special instructions or notes..."
            rows={4}
            data-testid="notes-textarea"
          />
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
          <Button
            variant="outline"
            onClick={() => navigate(`/proposal/${id}`)}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            data-testid="save-button"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditProposal;
